/**
 * What the Record payment dialog (RecordPaymentModal.vue) shows: the Method segments, the name of
 * the reference box, the save button's text and the amount check. The request bodies live in
 * paymentPayload.ts.
 */
import { formatMoney } from '@/lib/money'

/** The methods the dialog has always offered; the values are what is stored. */
export const PAYMENT_METHODS = ['bKash', 'Nagad', 'Bank Transfer', 'Cash', 'Other'] as const

const SHORT_LABELS: Record<string, string> = { 'Bank Transfer': 'Bank' }

/**
 * The Method segments. A stored method outside the list (an old "Rocket") is added at the end, so
 * an edit shows it selected and sends it back unchanged.
 */
export function methodOptions(selected: string): { value: string; label: string }[] {
  const values: string[] = [...PAYMENT_METHODS]
  if (selected && !values.includes(selected)) values.push(selected)
  return values.map((value) => ({ value, label: SHORT_LABELS[value] ?? value }))
}

/** The segment a stored method selects: matched case-insensitively (`bkash` → bKash), else itself. */
export function matchMethod(stored: string): string {
  return PAYMENT_METHODS.find((m) => m.toLowerCase() === stored.toLowerCase()) ?? stored
}

const REFERENCE_LABELS: Record<string, string> = {
  bKash: 'bKash transaction ID',
  Nagad: 'Nagad transaction ID',
  'Bank Transfer': 'Bank reference',
  Cash: 'Receipt number',
}

/** The reference box is named after the method: what that method calls its number. */
export function referenceLabel(method: string): string {
  return REFERENCE_LABELS[matchMethod(method)] ?? 'Reference number'
}

function hasAmount(amount: unknown): amount is number {
  return typeof amount === 'number' && Number.isFinite(amount) && amount > 0
}

/** "Record ৳2,899" once there is an amount; "Save changes" for an edit. */
export function submitLabel(editing: boolean, amount: unknown, currency: string): string {
  if (editing) return 'Save changes'
  return hasAmount(amount) ? `Record ${formatMoney(amount, currency)}` : 'Record payment'
}

/** The amount must be above zero (an empty number box gives ''). */
export function amountError(amount: unknown): string {
  return hasAmount(amount) ? '' : 'Enter the amount received.'
}
